import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { authService } from '../services/auth.service';
import { Input } from '../components/Input';
import { PasswordInput } from '../components/PasswordInput';
import { Button } from '../components/Button';
import { Logo } from '../components/Logo';
import { PasswordRequirements } from '../components/PasswordRequirements';
import { COLORS } from '../../../core/constants/colors';

export const InvitePasswordResetPage: React.FC = () => {
  const { resetPasswordWithTemporary } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    temporaryPassword: '',
    newPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password requirements
    const passwordErrors: string[] = [];
    if (formData.newPassword.length < 8) {
      passwordErrors.push('Password must be at least 8 characters');
    }
    if (!/[A-Z]/.test(formData.newPassword)) {
      passwordErrors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(formData.newPassword)) {
      passwordErrors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(formData.newPassword)) {
      passwordErrors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.newPassword)) {
      passwordErrors.push('Password must contain at least one special character');
    }
    
    if (passwordErrors.length > 0) {
      setError(passwordErrors[0]);
      return;
    }

    setLoading(true);

    try {
      await resetPasswordWithTemporary({
        email: formData.email,
        temporaryPassword: formData.temporaryPassword,
        newPassword: formData.newPassword,
      });
      // Role/company-aware redirect: system admins -> admin dashboard; users who
      // already belong to a company (partner admins or members) -> dashboard;
      // brand-new users with no company -> enrichment onboarding.
      let dest = '/enrichment';
      try {
        const user = await authService.getCurrentUser();
        const isAdmin = user.permissions?.is_admin === true ||
          user.groups?.includes('admin') || false;
        const hasCompany = !!user.company_id ||
          user.has_company_admin_access === true ||
          (user.administered_companies?.length ?? 0) > 0 ||
          user.has_company_access === true ||
          (user.member_companies?.length ?? 0) > 0;
        dest = isAdmin ? '/admin' : hasCompany ? '/dashboard' : '/enrichment';
      } catch {
        // fall through to enrichment if user info can't be resolved
      }
      navigate(dest, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Password reset failed. Please check your temporary password and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: COLORS.extended.cultured }}
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
          <Logo />
          
          <div className="text-center mb-8">
            <h1
              className="text-3xl md:text-4xl font-bold mb-2"
              style={{ color: COLORS.primary.persianIndigo }}
            >
              Set Your Password
            </h1>
            <p
              className="text-sm"
              style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}
            >
              Enter your email, temporary password, and choose a new password
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your.email@example.com"
              required
            />

            <PasswordInput
              label="Temporary Password"
              value={formData.temporaryPassword}
              onChange={(e) => setFormData({ ...formData, temporaryPassword: e.target.value })}
              placeholder="Enter temporary password from email"
              required
            />

            <div
              className="relative"
              onMouseEnter={() => setShowPasswordRequirements(true)}
              onMouseLeave={() => setShowPasswordRequirements(false)}
            >
              <PasswordInput
                label="New Password"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                placeholder="Enter your new password"
                required
              />
              <PasswordRequirements password={formData.newPassword} show={showPasswordRequirements} />
            </div>

            {error && (
              <div
                className="mb-4 p-3 rounded-lg"
                style={{ backgroundColor: COLORS.extended.redSalsa + '20' }}
              >
                <p className="text-sm" style={{ color: COLORS.extended.redSalsa }}>
                  {error}
                </p>
              </div>
            )}

            <Button type="submit" disabled={loading}>
              {loading ? 'Setting Password...' : 'Set Password'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-semibold hover:underline"
              style={{ color: COLORS.user.queryButton }}
            >
              ← Back to login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

